import {useSecureApi} from '@hconnect/common/hooks/useApi'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import moment from 'moment'
import {useMemo} from 'react'
import {useQuery, UseQueryOptions} from 'react-query'

import {MINUTES_INTERVAL} from './constants'
import {TimeSlot} from './types'
import {useFeatureSlotManagementForRmcOrderIntakeEnabled} from './useFeatureFlag'
import {useOffHoursSlots} from './useOffHoursSlots'

export type AvailableSlotsParams = {
  shippingDate: string
  contractNumber: string
  contractItemNumber: string
  siteNumber: string
  quantity: number
  truckType?: string
  unloadingMethod?: string
  orgUnitId: string
  loadSpacing?: string
  flowRate?: string
  totalTimeOnJobSite?: string
  plusLoadExpected?: boolean
}

export type AvailableSlotsResponse = {
  error?: string
  errorCode?: string
  availableSlots: string[]
}

const ERROR_CODES = {
  isOutOfAutoConfirmLeadTime: 'insufficientLeadTime'
}

export const useAvailableSlots = (params: AvailableSlotsParams) => {
  const secureApi = useSecureApi()
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()

  const orderDateTime = params.shippingDate
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')

  const queryOptions: UseQueryOptions<AvailableSlotsResponse, Error> = {
    refetchInterval: 90 * 1000, // Refetch every 90 seconds
    cacheTime: 90 * 1000,
    staleTime: 90 * 1000,
    retry: 0,
    enabled: slotManagementEnabled // Only fetch if slot management is enabled
  }

  const paramsWithAdjustedDate = useMemo(() => {
    const adjustedShippingDate = moment(params.shippingDate)
      .startOf('day')
      .add(2, 'hours')
      .toISOString()

    return {
      ...params,
      shippingDate: adjustedShippingDate
    }
  }, [params])

  const getSlots = async (): Promise<AvailableSlotsResponse> => {
    try {
      // Track slot management API call
      trackEvent('rmcSlotManagementApiCall', {
        businessLine: 'RMC',
        shippingDate: paramsWithAdjustedDate.shippingDate,
        contractNumber: paramsWithAdjustedDate.contractNumber,
        siteNumber: paramsWithAdjustedDate.siteNumber,
        quantity: paramsWithAdjustedDate.quantity,
        unloadingMethod: paramsWithAdjustedDate.unloadingMethod,
        hasTimeOnJobSite: !!paramsWithAdjustedDate.totalTimeOnJobSite,
        hasLoadSpacing: !!paramsWithAdjustedDate.loadSpacing,
        hasFlowRate: !!paramsWithAdjustedDate.flowRate
      })

      const response = await secureApi.get('/rmcSlots/available', {params: paramsWithAdjustedDate})
      const data = response.data ?? {availableSlots: []}

      // Track successful slot retrieval
      trackEvent('rmcSlotManagementApiSuccess', {
        businessLine: 'RMC',
        slotsReturned: data.availableSlots?.length || 0,
        hasError: !!data.error
      })

      return data
    } catch (error) {
      console.error('Error fetching available slots:', error)

      // Track API failure
      trackEvent('rmcSlotManagementApiError', {
        businessLine: 'RMC',
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      // Return empty slots array on error
      return {availableSlots: []}
    }
  }

  const queryInfo = useQuery<AvailableSlotsResponse, Error>(
    ['available-slots', paramsWithAdjustedDate],
    getSlots,
    queryOptions
  )

  // Generate all possible time slots within earliest and latest load time (15-minute intervals)
  const allTimeSlots = useMemo((): TimeSlot[] => {
    const businessHours = dateTimeConfiguration.businessHours
    if (!businessHours?.earliestPossibleLoadTime || !businessHours?.latestPossibleLoadTime) {
      return []
    }

    const startTime = moment(orderDateTime)
      .startOf('day')
      .add(moment.duration(businessHours.earliestPossibleLoadTime))
    const endTime = moment(orderDateTime)
      .startOf('day')
      .add(moment.duration(businessHours.latestPossibleLoadTime))

    const slots: TimeSlot[] = []
    const current = startTime.clone()

    while (current.isSameOrBefore(endTime)) {
      const timeSlot = current.format('HH:mm')
      // All slots within business hours are ALWAYS available for selection
      // regardless of auto-confirm lead time or instant approval status
      const isAvailable = true

      slots.push({
        slot: timeSlot,
        isAvailable
      })

      current.add(MINUTES_INTERVAL, 'minutes')
    }

    return slots
  }, [orderDateTime, dateTimeConfiguration.businessHours])

  const isOutOfAutoConfirmLeadTime =
    queryOptions.enabled && queryInfo?.data?.errorCode === ERROR_CODES.isOutOfAutoConfirmLeadTime

  const instantApprovalUnavailable =
    queryOptions.enabled &&
    !isOutOfAutoConfirmLeadTime &&
    (queryInfo.isError || (!!queryInfo.data && !!queryInfo.data.error))

  // availableSlots = instant approval slots (from API)
  const instantApprovalSlots = (queryInfo.data?.availableSlots ?? []).map((slot) =>
    moment(slot).format('HH:mm')
  )

  // For backward compatibility, return instant approval slots as availableSlots
  const availableSlots = instantApprovalSlots
  const hasAtLeastOneAvailableSlot = availableSlots.length > 0
  const hasAtLeastOneUnavailableSlot = false // All slots within business hours are always available

  const {offHoursSlots} = useOffHoursSlots({shippingDate: params.shippingDate})
  const orderDateTimeMoment = moment(orderDateTime).format('HH:mm')
  const showBannerWhenSlotIsUnavailable =
    !instantApprovalUnavailable &&
    !isOutOfAutoConfirmLeadTime &&
    !queryInfo.isLoading &&
    !instantApprovalSlots.includes(orderDateTimeMoment)

  const isSelectedSlotAvailable =
    !instantApprovalUnavailable &&
    !isOutOfAutoConfirmLeadTime &&
    instantApprovalSlots.includes(orderDateTimeMoment)

  return {
    allTimeSlots, // All possible time slots within earliest and latest load times
    availableSlots, // Only instant approval slots
    offHoursSlots,
    hasAtLeastOneAvailableSlot,
    hasAtLeastOneUnavailableSlot,
    isLoading: queryInfo.isLoading,
    isFetching: queryInfo.isFetching,
    isError: queryInfo.isError,
    error: queryInfo.error,
    refetch: queryInfo.refetch,
    selectedSlot: moment(orderDateTime).format('YYYY-MM-DD HH:mm'),
    isOutOfAutoConfirmLeadTime: isOutOfAutoConfirmLeadTime ?? false,
    instantApprovalUnavailable: instantApprovalUnavailable ?? false,
    showBannerWhenSlotIsUnavailable,
    isSelectedSlotAvailable
  }
}
